import React, { useState, useEffect } from 'react';
import { Flame, Save, RefreshCw, Plus, Trash2, RotateCcw } from 'lucide-react';
import api from '../services/api';
import { clearHeatmapConfigCache } from '../hooks/useHeatmapConfig';
import {
  DEFAULT_HEATMAP_GRADIENT,
  HEATMAP_GRADIENT_PRESETS,
  parseRgbaColor,
  rgbaToString,
  rgbToHex,
  hexToRgb,
  gradientToCssBackground,
} from '../utils/heatmapUtils';

function gradientToStops(gradient) {
  return gradient.map((color, index) => {
    const parsed = parseRgbaColor(color) || { r: 0, g: 0, b: 0, a: 1 };
    return {
      id: index,
      hex: rgbToHex(parsed.r, parsed.g, parsed.b),
      alpha: Math.round(parsed.a * 100),
    };
  });
}

function stopsToGradient(stops) {
  return stops.map(stop => {
    const rgb = hexToRgb(stop.hex) || { r: 0, g: 0, b: 0 };
    return rgbaToString({ ...rgb, a: stop.alpha / 100 });
  });
}

function HeatmapConfigManager() {
  const [config, setConfig] = useState({
    default_radius: 40,
    opacity: 0.85,
    show_clusters_default: true,
    gradient_stops: gradientToStops(DEFAULT_HEATMAP_GRADIENT),
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getHeatmapConfig();
      if (response.config) {
        const cfg = response.config;
        const gradient = cfg.gradient || DEFAULT_HEATMAP_GRADIENT;
        setConfig({
          default_radius: cfg.defaultRadius ?? cfg.default_radius ?? 40,
          opacity: cfg.opacity ?? 0.85,
          show_clusters_default: cfg.showClustersDefault ?? cfg.show_clusters_default ?? true,
          gradient_stops: gradientToStops(gradient),
        });
      }
    } catch (err) {
      console.error('Error loading heatmap config:', err);
      setError('Failed to load heatmap configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await api.updateHeatmapConfig({
        default_radius: config.default_radius,
        opacity: config.opacity,
        show_clusters_default: config.show_clusters_default,
        gradient: stopsToGradient(config.gradient_stops),
      });

      clearHeatmapConfigCache();
      setSuccess('Heatmap settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving heatmap config:', err);
      setError(err.message || 'Failed to save heatmap configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetGradient = () => {
    setConfig(prev => ({
      ...prev,
      gradient_stops: gradientToStops(DEFAULT_HEATMAP_GRADIENT),
    }));
  };

  const applyGradientPreset = gradient => {
    setConfig(prev => ({
      ...prev,
      gradient_stops: gradientToStops(gradient),
    }));
  };

  const updateStop = (index, field, value) => {
    setConfig(prev => {
      const stops = [...prev.gradient_stops];
      stops[index] = { ...stops[index], [field]: value };
      return { ...prev, gradient_stops: stops };
    });
  };

  const addStop = () => {
    setConfig(prev => {
      if (prev.gradient_stops.length >= 15) return prev;
      const last = prev.gradient_stops[prev.gradient_stops.length - 1];
      return {
        ...prev,
        gradient_stops: [
          ...prev.gradient_stops,
          { id: Date.now(), hex: last?.hex || '#ff0000', alpha: last?.alpha ?? 100 },
        ],
      };
    });
  };

  const removeStop = index => {
    setConfig(prev => {
      if (prev.gradient_stops.length <= 3) return prev;
      return {
        ...prev,
        gradient_stops: prev.gradient_stops.filter((_, i) => i !== index),
      };
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow mt-8 p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading heatmap settings...</span>
        </div>
      </div>
    );
  }

  const previewGradient = gradientToCssBackground(stopsToGradient(config.gradient_stops));
  return (
    <div className="bg-white rounded-lg shadow mt-8">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <Flame className="w-5 h-5 text-orange-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Heatmap Settings</h3>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Configure colour gradient, default radius, and overlay behaviour for incident heatmaps.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Display defaults</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default radius ({config.default_radius}m)
              </label>
              <input
                type="range"
                min="10"
                max="200"
                step="5"
                value={config.default_radius}
                onChange={e =>
                  setConfig(prev => ({
                    ...prev,
                    default_radius: parseInt(e.target.value, 10),
                  }))
                }
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Initial radius when users open the heatmap on the main map.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overlay opacity ({Math.round(config.opacity * 100)}%)
              </label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={config.opacity}
                onChange={e =>
                  setConfig(prev => ({
                    ...prev,
                    opacity: parseFloat(e.target.value),
                  }))
                }
                className="w-full"
              />
            </div>
          </div>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.show_clusters_default}
              onChange={e =>
                setConfig(prev => ({
                  ...prev,
                  show_clusters_default: e.target.checked,
                }))
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show cluster counts on heatmap by default</span>
          </label>
        </div>

        <div className="space-y-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">Colour gradient</h4>
            <button
              type="button"
              onClick={handleResetGradient}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset to default
            </button>
          </div>

          <div>
            <div className="text-sm text-gray-600 mb-2">Presets</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(HEATMAP_GRADIENT_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => applyGradientPreset(preset.gradient)}
                  className="flex flex-col items-stretch rounded-md border border-gray-200 overflow-hidden hover:border-blue-400 hover:ring-1 hover:ring-blue-200 transition-colors text-left"
                  title={preset.label}
                >
                  <div
                    className="h-6 w-32"
                    style={{ background: gradientToCssBackground(preset.gradient) }}
                  />
                  <span className="px-2 py-1 text-xs text-gray-700 bg-gray-50">{preset.label}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Google classic matches the old Maps heatmap (green = sparse, red = dense). Apply a
              preset then Save to update the live map.
            </p>
          </div>

          <div>
            <div className="text-sm text-gray-600 mb-2">Preview</div>
            <div
              className="h-8 rounded-md border border-gray-200"
              style={{ background: previewGradient }}
            />
          </div>

          <div className="space-y-3">
            {config.gradient_stops.map((stop, index) => (
              <div key={stop.id ?? index} className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-gray-500 w-12">Stop {index + 1}</span>
                <input
                  type="color"
                  value={stop.hex}
                  onChange={e => updateStop(index, 'hex', e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded border border-gray-300"
                />
                <div className="flex items-center gap-2 flex-1 min-w-[160px]">
                  <span className="text-xs text-gray-500">Alpha</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={stop.alpha}
                    onChange={e => updateStop(index, 'alpha', parseInt(e.target.value, 10))}
                    className="flex-1"
                  />
                  <span className="text-xs text-gray-500 w-10">{stop.alpha}%</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeStop(index)}
                  disabled={config.gradient_stops.length <= 3}
                  className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove colour stop"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {config.gradient_stops.length < 15 && (
            <button
              type="button"
              onClick={addStop}
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add colour stop
            </button>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save heatmap settings
          </button>
        </div>
      </div>
    </div>
  );
}

export default HeatmapConfigManager;
